import { useCallback, useEffect, useState } from 'react'
import * as db from './db'
import type { DayKey, Entry, ID } from './types'

/**
 * Reads that go beyond the caller's own time.
 *
 * The workspace snapshot deliberately carries only your own entries, because
 * every mutation refetches it and an admin loading the whole team's history
 * to start a timer is the single most expensive thing this app did. Anything
 * that genuinely needs other people's time asks for it here instead --
 * always bounded, and only on the screens that show it.
 */

/** Lifetime seconds per project, aggregated by the database. */
export function useProjectTotals(workspaceId: ID) {
  const [totals, setTotals] = useState<Map<ID, db.ProjectTotal>>(new Map())

  const reload = useCallback(async () => {
    try {
      const rows = await db.listProjectTotals(workspaceId)
      setTotals(new Map(rows.map((r) => [r.projectId, r])))
    } catch {
      // A failure here costs a number on screen, not the screen itself.
      setTotals(new Map())
    }
  }, [workspaceId])

  // The other hooks in this file all guard against a late reply from a
  // workspace the caller has since left; this one did not, so switching
  // workspaces quickly could let the old totals land last and win.
  useEffect(() => {
    let cancelled = false

    void db
      .listProjectTotals(workspaceId)
      .then((rows) => {
        if (!cancelled) setTotals(new Map(rows.map((r) => [r.projectId, r])))
      })
      .catch(() => {
        if (!cancelled) setTotals(new Map())
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId])

  return { totals, reload }
}

/**
 * Entries across the team for one date range.
 *
 * `enabled` exists so a screen only pays for this when it is actually showing
 * other people -- Reports on "just me", or a timesheet of your own week, cost
 * nothing extra.
 */
export function useRangeEntries(
  workspaceId: ID,
  range: { from: DayKey; to: DayKey; userId?: ID } | null,
  enabled: boolean,
) {
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(false)

  const from = range?.from
  const to = range?.to
  const userId = range?.userId

  useEffect(() => {
    if (!enabled || !from || !to) return

    let cancelled = false
    setLoading(true)

    void db
      .listEntries(workspaceId, { from, to, userId })
      .then((rows) => {
        if (!cancelled) setEntries(rows)
      })
      .catch(() => {
        if (!cancelled) setEntries([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId, from, to, userId, enabled])

  // Derived rather than cleared in the effect: a disabled hook has nothing to
  // show, and saying so here means no render happens just to empty the state.
  return { entries: enabled ? entries : [], loading: enabled && loading }
}

/**
 * What the entries in a range are worth, keyed by entry id.
 *
 * Fetched for BOTH scopes, unlike useRangeEntries. "Just me" reads entries
 * from the workspace snapshot, and that snapshot carries no prices at all --
 * so skipping the query when the scope is narrow would leave exactly the
 * screen that shows one person's money with no money to show.
 *
 * RLS scopes the view the same way it scopes everything else: a member gets
 * their own rows, an admin gets the team's.
 */
export function useRangeBilling(
  workspaceId: ID,
  range: { from: DayKey; to: DayKey } | null,
  enabled: boolean,
  /**
   * Anything that should force the prices to be asked for again.
   *
   * The view is the only thing that knows what an entry is worth, so a screen
   * that EDITS entries leaves this map stale the moment it does: a row toggled
   * non-billable, re-timed or given a fixed fee would otherwise sit there
   * wearing the figure it used to have. Such a caller passes a signature of
   * the rows it is showing. Reports omits it -- it only reads -- and keeps
   * refetching on the range alone.
   */
  revision?: string,
) {
  const [billing, setBilling] = useState<Map<ID, db.BillingRow>>(new Map())
  const [loading, setLoading] = useState(false)

  const from = range?.from
  const to = range?.to

  useEffect(() => {
    if (!enabled || !from || !to) return

    let cancelled = false
    setLoading(true)

    void db
      .listBilling(workspaceId, { from, to })
      .then((rows) => {
        if (!cancelled) setBilling(new Map(rows.map((r) => [r.entryId, r])))
      })
      .catch(() => {
        // An empty map is not "everything is free" -- the screen reads a miss
        // as "no figure yet" and says so, rather than printing a confident 0.
        if (!cancelled) setBilling(new Map())
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId, from, to, enabled, revision])

  return {
    billing: enabled ? billing : new Map<ID, db.BillingRow>(),
    loading: enabled && loading,
  }
}

/**
 * How many entries the whole workspace holds.
 *
 * Only the delete confirmations want this, and only a manager sees them, so
 * `enabled` keeps everyone else from paying for the query. Null means the
 * number is not known yet -- callers should word themselves without it rather
 * than claim a confident zero before the answer arrives.
 */
export function useEntryCount(workspaceId: ID, enabled: boolean) {
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    void db
      .countEntries(workspaceId)
      .then((n) => {
        if (!cancelled) setCount(n)
      })
      .catch(() => {
        if (!cancelled) setCount(null)
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId, enabled])

  return enabled ? count : null
}

/**
 * Whose timer is running, refreshed while the screen is open.
 *
 * Polled rather than pushed: a websocket for six rows is more moving parts
 * than the answer is worth, and the cost here is bounded by the screen being
 * open -- close the tab and it stops. Thirty seconds is close enough for a
 * dot that means "on the clock now" and far enough apart to be invisible
 * against the snapshot refetches that happen on every mutation anyway.
 */
export function useRunningTimers(workspaceId: ID, enabled: boolean) {
  const [running, setRunning] = useState<Map<ID, db.RunningNow>>(new Map())

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    const load = async () => {
      try {
        const rows = await db.listRunningTimers(workspaceId)
        if (!cancelled) setRunning(new Map(rows.map((r) => [r.userId, r])))
      } catch {
        // A dot that fails to load is a missing dot, not a broken screen.
        if (!cancelled) setRunning(new Map())
      }
    }

    void load()
    const id = setInterval(() => void load(), 30_000)

    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [workspaceId, enabled])

  return enabled ? running : new Map<ID, db.RunningNow>()
}
